import httpClient, { handleAPIError, createSuccessResponse } from './httpClient.js';

/**
 * EventsAPI - Handles GDELT news and USGS earthquake data
 *
 * Uses smart-refresh endpoints that:
 * - Return cached data immediately if fresh (<24h old)
 * - Fetch from external APIs only when cache is stale
 */
export class EventsAPI {
  constructor() {
    this.client = httpClient;
  }

  /**
   * Fetch GDELT news events for a mine with smart caching
   * Returns cached data if <24h old, otherwise fetches fresh
   *
   * @param {number} mineId - Mine ID
   * @param {Object} options - Optional parameters
   * @param {number} options.days - Number of days of news to return (default: 30)
   * @param {number} options.maxAge - Max cache age in hours before refresh (default: 24)
   * @returns {Promise<Object>} Response with news events and cache status
   */
  async getNewsForMine(mineId, options = {}) {
    const { days = 30, maxAge = 24 } = options;

    try {
      const response = await this.client.get(`/gdelt/news/smart/${mineId}`, {
        params: { days, maxAge }
      });

      return createSuccessResponse(response.data);
    } catch (error) {
      return handleAPIError(error, {
        operation: 'getNewsForMine',
        mineId
      });
    }
  }

  /**
   * Fetch USGS earthquake data for a mine with smart caching
   * Returns cached data if <24h old, otherwise fetches fresh
   *
   * @param {number} mineId - Mine ID
   * @param {Object} options - Optional parameters
   * @param {number} options.days - Number of days of data to return (default: 30)
   * @param {number} options.maxAge - Max cache age in hours before refresh (default: 24)
   * @returns {Promise<Object>} Response with earthquake data and cache status
   */
  async getEarthquakesForMine(mineId, options = {}) {
    const { days = 30, maxAge = 24 } = options;

    try {
      const response = await this.client.get(`/usgs/earthquakes/smart/${mineId}`, {
        params: { days, maxAge }
      });

      return createSuccessResponse(response.data);
    } catch (error) {
      return handleAPIError(error, {
        operation: 'getEarthquakesForMine',
        mineId
      });
    }
  }

  /**
   * Fetch both news and earthquake data for a mine in parallel
   * Useful when opening mine details panel
   *
   * @param {number} mineId - Mine ID
   * @param {Object} options - Optional parameters
   * @returns {Promise<Object>} Combined response with news and earthquakes
   */
  async getMineEvents(mineId, options = {}) {
    try {
      const [newsResult, earthquakesResult] = await Promise.allSettled([
        this.getNewsForMine(mineId, options),
        this.getEarthquakesForMine(mineId, options)
      ]);

      return {
        success: true,
        data: {
          news: newsResult.status === 'fulfilled' ? newsResult.value.data : null,
          earthquakes: earthquakesResult.status === 'fulfilled' ? earthquakesResult.value.data : null,
          errors: {
            news: newsResult.status === 'rejected' ? newsResult.reason?.message : null,
            earthquakes: earthquakesResult.status === 'rejected' ? earthquakesResult.reason?.message : null
          }
        }
      };
    } catch (error) {
      return handleAPIError(error, {
        operation: 'getMineEvents',
        mineId
      });
    }
  }
}

// Export singleton instance
export const eventsAPI = new EventsAPI();
